import type { RenderTextBox } from '@lite-workspace/shared/canvas-engine/core';
import { CARET_BASELINE_DESCENT_RATIO } from './caret-metrics';
import { planGlyphsOnPath, resolveCapHeightRatio, type PathGlyphPlacement } from './glyph-layout-backend';
import type { Shaper } from './glyph-shaper';
import type { ShapedCaretLayout } from './overlay-text-edit';

interface Point {
  x: number;
  y: number;
}

/** Caret grid of text on a path, in box px: the engine's own glyph placements plus the caret's vertical reach. */
export interface PathCaretLayout {
  glyphs: readonly PathGlyphPlacement[];
  home: { x: number; y: number; angle: number };
  /** Above the baseline — the engine's cap line. */
  ascent: number;
  descent: number;
}

export interface PathCaretSegment {
  top: Point;
  bottom: Point;
}

export interface PathCaretCell {
  tl: Point;
  tr: Point;
  br: Point;
  bl: Point;
}

/** Where the box's frame starts in book px and how many frame-local px one book px spans on each axis. */
export interface PathCaretFrame {
  x: number;
  y: number;
  kx: number;
  ky: number;
}

export function buildPathCaretLayout(box: RenderTextBox, shaper: Shaper, fontRef: string): PathCaretLayout | undefined {
  const plan = planGlyphsOnPath(box, shaper, fontRef);
  if (!plan) return undefined;
  return {
    glyphs: plan.glyphs,
    home: plan.home,
    ascent: resolveCapHeightRatio(shaper, fontRef) * plan.fontSize * plan.vScale,
    descent: plan.fontSize * CARET_BASELINE_DESCENT_RATIO * plan.vScale,
  };
}

const upOf = (angle: number): Point => ({ x: Math.sin(angle), y: -Math.cos(angle) });

// Right after the letter before the index, turned with that letter — on a sharp bend the caret stays with it.
function caretAnchor(layout: PathCaretLayout, index: number): { x: number; y: number; angle: number } {
  let after: PathGlyphPlacement | undefined;
  for (const g of layout.glyphs) if (g.charEnd <= index) after = g;
  if (after) {
    return {
      x: after.x + after.advance * Math.cos(after.angle),
      y: after.y + after.advance * Math.sin(after.angle),
      angle: after.angle,
    };
  }

  const first = layout.glyphs[0];
  return first ? { x: first.x, y: first.y, angle: first.angle } : layout.home;
}

export function pathCaretAt(layout: PathCaretLayout, index: number): PathCaretSegment {
  const { x, y, angle } = caretAnchor(layout, index);
  const up = upOf(angle);
  return {
    top: { x: x + up.x * layout.ascent, y: y + up.y * layout.ascent },
    bottom: { x: x - up.x * layout.descent, y: y - up.y * layout.descent },
  };
}

// The nearest glyph cell takes the click, its half decides the side — the browser's own rule, per rotated glyph.
export function pathCaretIndexAt(layout: PathCaretLayout, x: number, y: number): number {
  let best: PathGlyphPlacement | undefined;
  let bestDistance = Infinity;
  let bestAlong = 0;
  for (const g of layout.glyphs) {
    const cos = Math.cos(g.angle);
    const sin = Math.sin(g.angle);
    const along = (x - g.x) * cos + (y - g.y) * sin;
    const rise = (x - g.x) * sin - (y - g.y) * cos;
    const outAlong = along < 0 ? -along : Math.max(0, along - g.advance);
    const outRise = rise > layout.ascent ? rise - layout.ascent : Math.max(0, -layout.descent - rise);
    const distance = Math.hypot(outAlong, outRise);
    if (distance < bestDistance) {
      best = g;
      bestDistance = distance;
      bestAlong = along;
    }
  }

  if (!best) return 0;
  return bestAlong < best.advance / 2 ? best.charStart : best.charEnd;
}

// Glyph cell from the cap line to below the baseline, widened by `pad` on every side.
function cellOf(layout: PathCaretLayout, g: { x: number; y: number; angle: number; advance: number }, pad = 0): PathCaretCell {
  const along = { x: Math.cos(g.angle), y: Math.sin(g.angle) };
  const up = upOf(g.angle);
  const at = (a: number, rise: number): Point => ({ x: g.x + along.x * a + up.x * rise, y: g.y + along.y * a + up.y * rise });
  return {
    tl: at(-pad, layout.ascent + pad),
    tr: at(g.advance + pad, layout.ascent + pad),
    br: at(g.advance + pad, -layout.descent - pad),
    bl: at(-pad, -layout.descent - pad),
  };
}

export function pathSelectionCells(layout: PathCaretLayout, from: number, to: number): PathCaretCell[] {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  if (lo === hi) return [];
  return layout.glyphs.filter(g => g.charStart >= lo && g.charEnd <= hi).map(g => cellOf(layout, g));
}

/** A click beyond this share of the caret height from every letter is not aimed at the text. */
const REACH_SHARE = 0.3;

// Where a click still belongs to the text: every glyph cell with a margin; an emptied text keeps a cell at its caret.
export function pathTextReach(layout: PathCaretLayout): PathCaretCell[] {
  const pad = (layout.ascent + layout.descent) * REACH_SHARE;
  if (layout.glyphs.length === 0) return [cellOf(layout, { ...layout.home, advance: 0 }, pad)];
  return layout.glyphs.map(g => cellOf(layout, g, pad));
}

/** The grid on the overlay's terms: frame-local px, each caret and highlight band turned with its glyph. */
export function pathShapedCaret(layout: PathCaretLayout, textLength: number, frame: PathCaretFrame): ShapedCaretLayout {
  const toLocal = (p: Point): Point => ({ x: (p.x - frame.x) * frame.kx, y: (p.y - frame.y) * frame.ky });
  return {
    slotAt: index => {
      const { top, bottom } = pathCaretAt(layout, index);
      const a = toLocal(top);
      const b = toLocal(bottom);
      return { x: a.x, top: a.y, height: Math.hypot(b.x - a.x, b.y - a.y), angle: Math.atan2(a.x - b.x, b.y - a.y) };
    },
    indexAt: (x, y) => pathCaretIndexAt(layout, x / frame.kx + frame.x, y / frame.ky + frame.y),
    // One line along the curve: ↑/↓ have no line to step to, so the caret goes to the text edges.
    indexByLineStep: () => undefined,
    lineEdge: (_index, edge) => (edge === 'start' ? 0 : textLength),
    bandsFor: (from, to) =>
      pathSelectionCells(layout, from, to).map(cell => {
        const tl = toLocal(cell.tl);
        const tr = toLocal(cell.tr);
        const bl = toLocal(cell.bl);
        return {
          x: tl.x,
          top: tl.y,
          width: Math.hypot(tr.x - tl.x, tr.y - tl.y),
          height: Math.hypot(bl.x - tl.x, bl.y - tl.y),
          angle: Math.atan2(tr.y - tl.y, tr.x - tl.x),
        };
      }),
    hitArea: pathTextReach(layout).map(cell => [cell.tl, cell.tr, cell.br, cell.bl].map(toLocal)),
  };
}
